import { Suspense, useEffect, useMemo } from 'react';

import { Html } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';

import { useAppDispatch, useAppSelector } from '../../../hooks.ts';
import { useStore } from '../../../pages/workspace/hooks.ts';
import { loadWireframeFiles } from '../../../store/reducers/workspace.reducer.ts';
import { getUser } from '../../../store/selectors/user.selector.ts';
import { selectLampWireframeMap } from '../../../store/selectors/workspace.selector.ts';
import { LoadingSpinner } from '../../loading/LoadingSpinner.tsx';
import { Lamp } from './Lamp.tsx';
import { MeasurementLine } from './MeasurementLine.tsx';
import { ModelTexture } from './ModelTexture.tsx';
import { ReferenceImagePlane } from './ReferenceImagePlane.tsx';
import { UploadedModel } from './UploadedModel.tsx';

export function Scene({ onModelLoad, modelsLoaded }: { onModelLoad?: () => void; modelsLoaded?: boolean }) {
  const {
    isLampCreationMode,
    lamps,
    uploadedModels,
    selectLamp,
    showIESModels,
    activeViewModes,
    activeVersion,
    isLampDistanceMode,
    lampDistancePoints,
    lampDistanceMeasured,
  } = useStore();

  const dispatch = useAppDispatch();
  const wireframeMap = useAppSelector(selectLampWireframeMap);
  const user = useAppSelector(getUser)!;
  const unitSystem = user.settings.units;

  const memoizedModels = useMemo(() => uploadedModels, [uploadedModels]);

  useEffect(() => {
    if (showIESModels && activeVersion?.id) {
      dispatch(loadWireframeFiles());
    }
  }, [dispatch, showIESModels, activeVersion?.id]);

  const handleCanvasClick = (event: ThreeEvent<MouseEvent>) => {
    if (!isLampCreationMode) {
      selectLamp('');
      return;
    }
    event.stopPropagation();
  };

  const isObjectMode = activeViewModes.object;
  const isRealTimeMode = activeViewModes.realTimeRender;
  const isMeasurementOrRenderMode = activeViewModes.measurement || activeViewModes.render;
  const showSceneModels = isObjectMode || isRealTimeMode;
  const shouldShowIESModels = showIESModels;

  const showLampVisuals = isObjectMode || (isMeasurementOrRenderMode && showIESModels);

  const shouldShowBulb = (lampUuid: string) => {
    if (isObjectMode && !showIESModels) return true;
    if (showIESModels && !wireframeMap.get(lampUuid)?.wireframe?.url) return true;
    return false;
  };

  return (
    <>
      <group position={[0, 0, 0]} onClick={handleCanvasClick}>
        {activeViewModes.render && (
          <Suspense
            fallback={
              <Html center>
                <LoadingSpinner />
              </Html>
            }
          >
            <ModelTexture />
          </Suspense>
        )}

        <ReferenceImagePlane />

        <group visible={showSceneModels}>
          {memoizedModels.map((model) => (
            <UploadedModel key={model.name} model={model} onLoad={onModelLoad} />
          ))}
        </group>

        {modelsLoaded &&
          !isRealTimeMode &&
          lamps.map((lamp) => (
            <Lamp
              key={lamp.id}
              id={lamp.id}
              position={lamp.position}
              name={lamp.name}
              isSelected={lamp.isSelected || false}
              showBulb={showLampVisuals && shouldShowBulb(lamp.uuid)}
              showIESModel={showLampVisuals && shouldShowIESModels}
              wireframe={wireframeMap.get(lamp.uuid)}
            />
          ))}
      </group>

      {isLampDistanceMode && lampDistancePoints.length > 0 && (
        <MeasurementLine
          points={lampDistancePoints}
          distance={lampDistanceMeasured}
          hoverPoint={null}
          isHoverSnapped={false}
          showCtrlHint={false}
          unitSystem={unitSystem}
        />
      )}
    </>
  );
}
